import { createApp, ref } from 'vue'
import { createPinia } from 'pinia'
import FloatingVue from 'floating-vue'
import * as Sentry from '@sentry/vue'

import { hasConsent, onConsentChange } from '@/shared/consent'
import { setupAnalytics } from '@/shared/analytics'

import 'material-design-iconic-font/dist/css/material-design-iconic-font.min.css'
import 'floating-vue/dist/style.css'
import App from './App.vue'
import router from './router'
import i18n, { loadLocale, getCurrentLocale } from './i18n'

import './styles/main.scss'
import { initTheme } from '@/composables/useTheme'
// Ловит `beforeinstallprompt` со старта: событие приходит один раз и раньше,
// чем человек откроет профиль с кнопкой «Установить приложение».
import '@/shared/composables/usePwaInstall'
import { apiClientPlugin } from '@/plugins/apiClient'
import { notificationsPlugin } from '@/plugins/notifications'

import type { TmaState } from '@/mini-app'
import { loadTelegramSdk } from '@/mini-app/telegramSdk'
import { readTelegramInitData } from '@/mini-app/telegramLaunchParams'
import { detectTelegramLaunch, resolveTmaTargetPath } from '@/mini-app/tmaLaunch'
import {
  logTmaAuthStart,
  logTmaAuthResponse,
  logTmaTokenExchange,
  logTmaAuthComplete,
  logTmaException,
  logTmaWarning,
  tmaAddBreadcrumb,
} from '@/mini-app/tmaDebugLogger'

// Global type declarations for skeleton timeout set in index.html
declare global {
  interface Window {
    __appLoadTimeout?: ReturnType<typeof setTimeout>
    __skeletonI18n?: { t: Record<string, string>; locale: string }
    __BUILD_VERSION__?: string
  }
}

/**
 * Cache busting: detect when browser has stale HTML that references old assets
 * This happens when CDN/browser caches old HTML but new assets are deployed
 * Solution: catch the error and force a hard reload to get fresh HTML
 * 
 * NOTE: Only enabled in production. In dev mode, these errors usually mean
 * the dev server needs to be restarted (Vite's "Outdated Optimize Dep")
 */
if (import.meta.env.PROD) {
  const RELOAD_ATTEMPTED_KEY = 'gonkagame_reload_attempted'

  const hasReloadBeenAttempted = (): boolean => {
    try {
      return sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === 'true'
    } catch {
      return false
    }
  }

  const markReloadAttempted = (): void => {
    try {
      sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, 'true')
    } catch {
      // Ignore storage errors
    }
  }

  const clearReloadAttempted = (): void => {
    try {
      sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY)
    } catch {
      // Ignore storage errors
    }
  }

  // Handle chunk/asset load errors (stale cache)
  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement | null

    // Необязательные скрипты (Telegram SDK) падают штатно — не перезагружаемся
    if (target?.dataset?.optional === 'true') return

    // Check if this is a script or link (CSS) load error
    if (target?.tagName === 'SCRIPT' || target?.tagName === 'LINK') {
      console.warn('[GonkaGame] Asset load failed, likely stale cache:', target)

      if (!hasReloadBeenAttempted()) {
        markReloadAttempted()
        console.log('[GonkaGame] Forcing hard reload to fetch fresh assets...')
        window.location.reload()
      }
    }
  }, true) // Use capture phase to catch errors before they bubble

  // Vite's own hook for a failed dynamic import (lazy route/component chunk
  // missing after a deploy). preventDefault() здесь оставлен: он гасит
  // ChunkLoadError в Sentry для каждой вкладки, пережившей деплой.
  //
  // А вот перезагрузки здесь больше нет, и это не упущение. Чанки страниц
  // грузит переход по маршруту, и лечить их сбой обязан router.onError: он
  // знает, КУДА человек шёл, и после повтора перезагружает страницу по
  // целевому адресу. Отсюда же уходил слепой reload() — он срабатывал первым
  // и возвращал человека в предыдущий раздел. Фоновая подкачка чанков
  // (prefetchNavigation.ts) по той же причине не имеет права перезагружать
  // страницу: её сбой человек вообще не должен замечать.
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('[GonkaGame] Vite preload error, likely stale cache:', event)
    event.preventDefault()
  })

  // Handle unhandled promise rejections (e.g., dynamic import failures)
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason

    // Check if this is a chunk load error (CSS preload or JS dynamic import)
    const isChunkLoadError = reason?.message?.includes('Unable to preload CSS') ||
      reason?.message?.includes('Failed to fetch dynamically imported module') ||
      reason?.message?.includes('Loading chunk') ||
      reason?.message?.includes('Loading CSS chunk')

    if (isChunkLoadError) {
      console.warn('[GonkaGame] Chunk load failed:', reason?.message)
      event.preventDefault() // Prevent error from propagating
    }
  })

  // Clear the reload flag once app successfully loads
  // This allows future deploys to trigger reload again
  window.addEventListener('load', () => {
    // Wait a bit to ensure all chunks loaded successfully
    setTimeout(clearReloadAttempted, 5000)
  })
} else {
  // Development mode: just log helpful message instead of reloading
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason

    if (reason?.message?.includes('Outdated Optimize Dep') ||
      reason?.message?.includes('Failed to fetch dynamically imported module')) {
      console.error(
        '%c[GonkaGame DEV] Vite dependency optimization is outdated. Please restart dev server: npm run dev',
        'color: #ff6b6b; font-weight: bold; font-size: 14px;'
      )
    }
  })
}


// Sync runtime theme state (the inline head script already applied data-theme
// before first paint; this attaches the OS listener and seeds reactive state).
initTheme()

// Сохранить запуск до .use(router): гостевой guard может убрать hash, путь и
// query. Здесь же, ДО первой навигации, снимается кука `tma_user` у обычного
// браузера — иначе гвард успеет увести его на /tma-init (см. tmaLaunch.ts).
const telegramLaunchInitData = readTelegramInitData()
const hasTelegramSignals = detectTelegramLaunch()
const telegramEntryPath = resolveTmaTargetPath(telegramLaunchInitData)

const app = createApp(App)

app
  .use(createPinia())
  .use(i18n)
  .use(apiClientPlugin as any)
  .use(notificationsPlugin)
  .use(FloatingVue)
  .use(router)

const sentryDsn = import.meta.env.VITE_GONKAGAME_SENTRY_DSN

/**
 * Мониторинг ошибок — необязательная категория «Поддержка и диагностика»
 * (`/law/services`): до согласия Sentry не поднимается вовсе, а не поднимается
 * «без личных данных». Согласие, данное позже, включает его в той же сессии —
 * поэтому инициализация вынесена в функцию и подписана на событие баннера.
 */
function initSentry() {
  Sentry.init({
    app,
    dsn: sentryDsn,
    // Release version for tracking errors by build  
    release: `gonkagame@${import.meta.env.VITE_BUILD_VERSION || 'dev'}`,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration()
    ],
    // Ignore external errors from Telegram WebApp API (when users open site via Telegram links)
    // and iziToast internal race conditions
    ignoreErrors: [
      /Method not found/i,
      /Error invoking/i,
      /postEvent/i,
      // iziToast internal errors (race conditions when toasts are removed during animation)
      /r\.children\[m\]\[h\]/i,
      /iziToast/i,
      // ResizeObserver errors (browser internal, not actionable)
      /ResizeObserver loop/i,
      // GeeTest CAPTCHA network errors (third-party service, user connectivity issues)
      /网络错误/i,  // "Network error" in Chinese
      /gt4\.js/i,
      // Chunk load errors (stale cache, handled by auto-reload in main.ts)
      /Unable to preload CSS/i,
      /Failed to fetch dynamically imported module/i,
      /Loading chunk/i,
      /Loading CSS chunk/i
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false
  })
}

/**
 * HK-IGRA.188 — Яндекс.Метрика. Своего условия на PROD здесь нет намеренно:
 * загрузку и без того держат два замка — номер счётчика из окружения (в
 * разработке и тестах его нет) и тумблер согласия «Аналитика». Третий замок
 * только маскировал бы отказ одного из первых двух.
 */
setupAnalytics()

if (import.meta.env.PROD && sentryDsn) {
  if (hasConsent('support')) {
    initSentry()
  } else {
    const stop = onConsentChange((state) => {
      if (!state.support) return
      stop()
      initSentry()
    })
  }
}

/**
 * Telegram Mini-App: detect and authenticate before mounting the app.
 *
 * When opened inside Telegram, performs auth via initData:
 * - success → exchanges token, stores authToken (router guard redirects to /dashboard)
 * - totp_required → stores state for TotpModal to pick up
 *
 * Provides reactive state via app.provide() for injection in components:
 * - 'tmaState' — current TMA flow state
 * - 'isInTelegram' — whether running inside Telegram
 */
const tmaState = ref<TmaState>({
  entryPath: telegramEntryPath,
  status: 'idle',
  initData: '',
  authResult: null,
  errorMessage: '',
})

// Признак Telegram — только настоящие признаки запуска (detectTelegramLaunch,
// mini-app/tmaLaunch.ts). Путь `/tma-init` признаком БОЛЬШЕ НЕ СЧИТАЕТСЯ: он
// запирал обычный браузер на этом экране кукой `tma_user` — почему именно,
// разобрано в шапке tmaLaunch.ts.
//
// NOTE: The `tma_user=1` cookie is intentionally NOT checked here.
// That cookie is only used by the router guard (router/index.ts) to redirect
// unauthenticated users back to /tma-init. Including it here would cause
// regular browser users who previously visited via TMA to be treated as TMA users,
// blocking profile loading and notification requests.
//
// Признак читается БЕЗ SDK: скрипт не стоит в <head> (116 КБ нужны только
// внутри Telegram), поэтому `window.Telegram` на этот момент ещё пуст.
const isInTelegram = hasTelegramSignals

/** Запасной источник initData: клиент положил его в window, минуя адрес. */
async function initDataFromSdk(): Promise<string | null> {
  const webApp = await loadTelegramSdk().catch(() => null)

  return readTelegramInitData(webApp?.initData || null)
}

/**
 * Фирменный цвет фона страницы — токен `--p-night-950` («bg/base»,
 * styles/tokens/_primitives.scss). Тот же цвет стоит в <meta name="theme-color">
 * и в манифесте. Держим литералом: main.ts работает до того, как применились
 * стили, и брать значение из CSS-переменной тут не у кого.
 */
const TMA_CHROME_COLOR = '#060a16'

/**
 * Обжить окно мини-аппа: сказать клиенту «готово», развернуть на всю высоту,
 * покрасить шапку в цвет фона, отключить нативный свайп-закрытие. Для этого
 * нужен SDK — и только для этого.
 *
 * Идёт САМО ПО СЕБЕ, вход её не ждёт: пока ready()/expand() и авторизация
 * висели на одном промисе, недоехавший скрипт означал «входа нет вообще».
 */
function setUpTelegramChrome(): void {
  void loadTelegramSdk()
    .then((webApp) => {
      webApp.ready()
      webApp.expand()

      // Шапку мини-аппа красит сам клиент Telegram, и по умолчанию она своего
      // цвета — светлой полосой поверх нашего тёмного фона. Красим её и зону
      // оверскролла в тот же фирменный фон, что и страница.
      // Обёрнуто так же, как свайпы: до Bot API 6.1 этих методов у клиента
      // просто нет, а старые клиенты умеют ругнуться на непонятный цвет —
      // запуск мини-аппа от этого падать не должен.
      try {
        if (typeof webApp.setHeaderColor === 'function') {
          webApp.setHeaderColor(TMA_CHROME_COLOR)
        }
        if (typeof webApp.setBackgroundColor === 'function') {
          webApp.setBackgroundColor(TMA_CHROME_COLOR)
        }
      } catch {
        // Старый клиент красить не умеет — остаётся со своей шапкой
      }

      // Disable the native TWA pull-to-close gesture (Bot API 7.7+)
      // Without this, scrolling up in nested scroll containers (e.g., TeamCardList)
      // triggers the TWA collapse instead of scrolling the content.
      // Wrapped in try-catch: on some Telegram client versions, the SDK's internal
      // integration.js may not be fully initialized yet, causing ReferenceError.
      try {
        webApp.disableVerticalSwipes?.()
      } catch {
        // Retry after SDK finishes initialization
        setTimeout(() => {
          try { webApp.disableVerticalSwipes?.() } catch { /* ignore */ }
        }, 500)
      }
    })
    .catch((sdkError: unknown) => {
      tmaAddBreadcrumb('main.ts: telegram sdk unavailable, window stays plain', {
        reason: sdkError instanceof Error ? sdkError.message : String(sdkError),
      }, 'warning')
    })
}

async function handleTelegramMiniAppAuth(): Promise<void> {
  setUpTelegramChrome()

  // initData берём ИЗ АДРЕСА — его дописывает клиент Telegram, и ждать для
  // этого скрипт не нужно. SDK спрашиваем только как запасной вариант: есть
  // клиенты, которые кладут window.Telegram сами, не тронув адрес.
  const initData = telegramLaunchInitData ?? readTelegramInitData() ?? await initDataFromSdk()
  if (!initData) {
    tmaAddBreadcrumb('main.ts: no initData, skipping auth', {}, 'warning')
    return
  }

  tmaState.value.status = 'authenticating'
  tmaState.value.initData = initData

  // --- Debug: log auth start ---
  logTmaAuthStart('main.ts')

  try {
    const { TelegramAuthService } = await import('@/mini-app/TelegramAuthService')
    const result = await TelegramAuthService.authenticate(initData)

    // --- Debug: log API response ---
    logTmaAuthResponse('main.ts', result as unknown as Record<string, unknown>)

    tmaState.value.authResult = result

    switch (result.result) {
      case 'success': {
        if (typeof result.spa_auth_token !== 'string' || !result.spa_auth_token.trim()) {
          tmaState.value.status = 'error'
          tmaState.value.errorMessage = 'No auth token received'
          logTmaWarning('Success response without spa_auth_token', 'main.ts', {
            result_code: result.result,
            user_id: (result as any).user_id ?? null,
          })
          logTmaAuthComplete('main.ts', 'error', 'Success without spa_auth_token')
          break
        }
        // Exchange one-time token for permanent session token
        try {
          const token = await TelegramAuthService.exchangeToken(result.spa_auth_token)
          logTmaTokenExchange('main.ts', true, token?.length)

          // Store auth token the same way as standard login/OAuth flow
          const { createTokenStorage } = await import('@space/shared-api-client')
          const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
          const tokenStorage = createTokenStorage({ storageKey })
          tokenStorage.setToken(token)

          tmaState.value.status = 'done'
          if (telegramEntryPath !== null) await router.replace(telegramEntryPath)
          logTmaAuthComplete('main.ts', 'done')
        } catch (exchangeErr) {
          logTmaTokenExchange('main.ts', false, undefined, exchangeErr)
          logTmaException(exchangeErr, 'main.ts', { phase: 'token_exchange' })
          tmaState.value.status = 'error'
          tmaState.value.errorMessage = exchangeErr instanceof Error ? exchangeErr.message : 'Token exchange failed'
          logTmaAuthComplete('main.ts', 'error', tmaState.value.errorMessage)
        }
        break
      }

      case 'totp_required':
        if (typeof result.spa_auth_token !== 'string' || !result.spa_auth_token.trim()) {
          tmaState.value.status = 'error'
          tmaState.value.errorMessage = i18n.global.t('tma.authError')
          await router.replace('/tma-init')
          break
        }
        tmaState.value.status = 'totp_required'
        // Завершение TOTP обрабатывает TmaInitPage, в том числе при первом
        // запуске без tma_user cookie, когда guard оставил нас на лендинге.
        await router.replace('/tma-init')
        logTmaAuthComplete('main.ts', 'totp_required')
        break

      default:
        tmaState.value.status = 'error'
        tmaState.value.errorMessage = result.message ?? 'Authentication failed'
        logTmaWarning('Unexpected auth result', 'main.ts', {
          result_code: result.result,
          message: result.message ?? null,
        })
        logTmaAuthComplete('main.ts', 'error', tmaState.value.errorMessage)
    }
  } catch (e: any) {
    tmaState.value.status = 'error'
    tmaState.value.errorMessage = e?.message ?? 'Network error'
    logTmaException(e, 'main.ts', { phase: 'authenticate' })
    logTmaAuthComplete('main.ts', 'error', tmaState.value.errorMessage)
  }
}

// Provide TMA state to all components
app.provide('tmaState', tmaState)
app.provide('isInTelegram', isInTelegram)

// Mount and hide skeleton after Vue is ready
router.isReady().then(async () => {
  // Ensure localized messages are loaded before mounting
  //
  // Без записи в cookie: эта строка только догружает словарь уже выбранного
  // языка, запоминать тут нечего. А на лендингах язык к этому моменту уже
  // принудительно русский (guard в router/index.ts) — и запись закрепила бы
  // его за посетителем навсегда, вместе с кабинетом.
  await loadLocale(getCurrentLocale(), false)

  // Telegram Mini-App: authenticate before mount to prevent landing page flash.
  // Ждём только при реальных признаках запуска из Telegram: обычный посетитель
  // (в том числе оказавшийся на /tma-init по старой ссылке) не должен ждать
  // скрипт с заблокированного в России telegram.org.
  if (hasTelegramSignals) {
    document.body.classList.add('tma-loading')
    // Update skeleton text for TMA context
    const loadingEl = document.getElementById('skeleton-loading-text')
    if (loadingEl) {
      const i18n = window.__skeletonI18n
      loadingEl.textContent = i18n?.locale === 'ru' ? 'Авторизация...' : 'Authenticating...'
    }

    await handleTelegramMiniAppAuth()
    document.body.classList.remove('tma-loading')
  }

  const mountEl = document.getElementById('igra-app')

  if (mountEl) {
    // Ensure the container is empty to prevent hydration mismatches
    mountEl.innerHTML = ''
    app.mount(mountEl)

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      // Clear the skeleton timeout since app loaded successfully
      if (window.__appLoadTimeout) {
        clearTimeout(window.__appLoadTimeout)
        delete window.__appLoadTimeout
      }

      const skeleton = document.getElementById('app-skeleton')
      if (skeleton) {
        skeleton.classList.add('hidden')
        setTimeout(() => skeleton.remove(), 300)
      }
    })
  } else {
    console.error('[GonkaGame] Mount element #igra-app not found')
  }
})
